/**
 * <s-countdown> — counts down to a date and time in a given time zone.
 *
 * Attributes:
 *   date      YYYY-MM-DD (required)
 *   time      HH:MM, 24-hour (default 00:00)
 *   timezone  IANA zone the date/time is in (default: the visitor's own zone)
 *
 * Expected DOM (rendered by blocks/countdown.liquid):
 *   <s-countdown>
 *     <div data-countdown-running> …[data-countdown-days|hours|minutes|seconds]… </div>
 *     <p data-countdown-ended hidden>…</p>
 *   </s-countdown>
 */
class SCountdown extends HTMLElement {
  connectedCallback() {
    this.stops = this.parseStops();
    this.target = this.stops.length ? this.nextStop() : this.resolveTarget();
    this.units = {
      days: this.querySelector("[data-countdown-days]"),
      hours: this.querySelector("[data-countdown-hours]"),
      minutes: this.querySelector("[data-countdown-minutes]"),
      seconds: this.querySelector("[data-countdown-seconds]"),
    };
    this.running = this.querySelector("[data-countdown-running]");
    this.ended = this.querySelector("[data-countdown-ended]");
    if (!Number.isFinite(this.target)) return;

    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Optional schedule: stops="YYYY-MM-DD HH:MM|Label;…". The countdown runs to the first stop still
   * ahead and writes its label into [data-countdown-label] inside the closest [data-countdown-group].
   */
  parseStops() {
    const raw = (this.getAttribute("stops") || "").trim();
    if (!raw) return [];
    return raw
      .split(";")
      .map((entry) => {
        const [when, ...label] = entry.split("|");
        const [date, time] = (when || "").trim().split(/\s+/);
        return { at: this.resolveTarget(date || "", time || "00:00"), label: label.join("|").trim() };
      })
      .filter((stop) => Number.isFinite(stop.at))
      .sort((a, b) => a.at - b.at);
  }

  /** Pick the first stop still ahead, show its label, and return its timestamp (the last stop once all have passed). */
  nextStop() {
    const now = Date.now();
    const stop = this.stops.find((s) => s.at > now) || this.stops[this.stops.length - 1];
    const label = this.closest("[data-countdown-group]")?.querySelector("[data-countdown-label]");
    if (label && stop.label && label.textContent !== stop.label) label.textContent = stop.label;
    return stop.at;
  }

  /** Convert the wall-clock date/time in `timezone` to a UTC timestamp. */
  resolveTarget(dateArg, timeArg) {
    const date = (dateArg ?? this.getAttribute("date") ?? "").trim();
    const time = (timeArg ?? this.getAttribute("time") ?? "00:00").trim() || "00:00";
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec(time) || [null, "0", "0"];
    if (!match) return NaN;

    const [y, mo, d] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
    const [h, mi] = [Number(timeMatch[1]), Number(timeMatch[2])];
    const wallClockAsUtc = Date.UTC(y, mo, d, h, mi);
    const zone = this.getAttribute("timezone");
    if (!zone) return new Date(y, mo, d, h, mi).getTime();

    try {
      // Offset of the zone at that moment; a second pass settles daylight-saving edges.
      let guess = wallClockAsUtc - this.offsetFor(zone, wallClockAsUtc);
      guess = wallClockAsUtc - this.offsetFor(zone, guess);
      return guess;
    } catch {
      return new Date(y, mo, d, h, mi).getTime();
    }
  }

  offsetFor(zone, timestamp) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(new Date(timestamp));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
    return asUtc - Math.floor(timestamp / 1000) * 1000;
  }

  tick() {
    let remaining = this.target - Date.now();
    if (remaining <= 0 && this.stops.length) {
      this.target = this.nextStop();
      remaining = this.target - Date.now();
    }
    if (remaining <= 0) {
      this.disconnectedCallback();
      if (this.running) this.running.hidden = true;
      if (this.ended) this.ended.hidden = false;
      return;
    }
    const total = Math.floor(remaining / 1000);
    const values = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };
    for (const [key, el] of Object.entries(this.units)) {
      if (!el) continue;
      const text = String(values[key]).padStart(2, "0");
      if (el.textContent !== text) el.textContent = text;
    }
  }
}

if (!customElements.get("s-countdown")) {
  customElements.define("s-countdown", SCountdown);
}
